import Pusher from 'pusher-js';

/**
 * Redux action representing websocket event
 * @typedef {string} SocketActionType
 */

/**
 * @constant
 */
var INIT = 'socket-service/INIT';

/**
 * @constant
 */
var SUBSCRIBE = 'socket-service/SUBSCRIBE';

/**
 * @constant
 */
var UNSUBSCRIBE = 'socket-service/UNSUBSCRIBE';

/**
 * @constant
 * @type {SocketActionType}
 */
var SESSION_EXPIRED = 'socket-service/SESSION_EXPIRED';

/**
 * @constant
 * @type {SocketActionType}
 */
var SEASON_STARTED = 'socket-service/SEASON_STARTED';

/**
 * @constant
 * @type {SocketActionType}
 */
var SEASON_ENDED = 'socket-service/SEASON_ENDED';

/**
 * @constant
 * @type {SocketActionType}
 */
var STORE_ITEM_DELETED = 'socket-service/STORE_ITEM_DELETED';

/**
 * @constant
 * @type {SocketActionType}
 */
var STORE_ITEM_UPDATED = 'socket-service/STORE_ITEM_UPDATED';

/**
 * @constant
 * @type {SocketActionType}
 */
var TABLE_UPDATED = 'socket-service/TABLE_UPDATED';

/**
 * @constant
 * @type {SocketActionType}
 */
var PROMOTION_ADDED = 'socket-service/PROMOTION_ADDED';

/**
 * @constant
 * @type {SocketActionType}
 */
var ITEM_EFFECT_EXPIRED = 'socket-service/ITEM_EFFECT_EXPIRED';

/**
 * @constant
 * @type {SocketActionType}
 */
var INVENTORY_ITEM_ADDED = 'socket-service/INVENTORY_ITEM_ADDED';

/**
 * @constant
 * @type {SocketActionType}
 */
var CAMPAIGN_STARTED = 'socket-service/CAMPAIGN_STARTED';

/**
 * @constant
 * @type {SocketActionType}
 */
var CAMPAIGN_ENDED = 'socket-service/CAMPAIGN_ENDED';

/**
 * @constant
 * @type {SocketActionType}
 */
var BET_BACK_PLACED = 'socket-service/BET_BACK_PLACED';

/**
 * @constant
 * @type {SocketActionType}
 */
var WORLD_SELECTED = 'socket-service/WORLD_SELECTED';

/**
 * @constant
 * @type {SocketActionType}
 */
var REGION_PROGRESS_CHANGED = 'socket-service/REGION_PROGRESS_CHANGED';

/**
 * @constant
 * @type {SocketActionType}
 */
var CAMPAIGNS_CHANGED = 'socket-service/CAMPAIGNS_CHANGED';

/**
 * @constant
 * @type {SocketActionType}
 */
var BALANCE_CHANGED = 'socket-service/BALANCE_CHANGED';

/**
 * @constant
 * @type {SocketActionType}
 */
var REGION_SELECTED = 'socket-service/REGION_SELECTED';

/**
 * @constant
 * @type {SocketActionType}
 */
var PROGRESS_CHANGED = 'socket-service/PROGRESS_CHANGED';

/**
 * @constant
 * @type {SocketActionType}
 */
var LOCKS_CHANGED = 'socket-service/LOCKS_CHANGED';

/**
 * @constant
 * @type {SocketActionType}
 */
var NEW_INVENTORY_ITEM_ADDED = 'socket-service/NEW_INVENTORY_ITEM_ADDED';

/**
 * @constant
 * @type {SocketActionType}
 */
var PROMOTION_STARTED = 'socket-service/PROMOTION_STARTED';

/**
 * @constant
 * @type {SocketActionType}
 */
var PROMOTION_ENDED = 'socket-service/PROMOTION_ENDED';

/**
 * @constant
 * @type {SocketActionType}
 */
var PROMOTION_PREVIEW_STARTED = 'socket-service/PROMOTION_PREVIEW_STARTED';

/**
 * @constant
 * @type {SocketActionType}
 */
var PROMOTION_PREVIEW_ENDED = 'socket-service/PROMOTION_PREVIEW_ENDED';

/**
 * Get deeply nested value
 * @private
 * @param {array} segments Array of properties.
 * @param {object} value Object to get nested property from.
 * @returns {?} Retrieved value or undefined
 */
function path(segments, value) {
  var prop = segments[0];

  if (prop == null || value == null) {
    return value;
  }

  return path(segments.slice(1), value[prop]);
}

// Pusher event types
var EVENT_MAPPINGS = {
  balanceChanged: BALANCE_CHANGED,
  betBackPlaced: BET_BACK_PLACED,
  campaignChanged: CAMPAIGNS_CHANGED,
  ctoolCampaignEnded: CAMPAIGN_ENDED,
  ctoolCampaignStarted: CAMPAIGN_STARTED,
  itemAdded: NEW_INVENTORY_ITEM_ADDED,
  itemEffectExpired: ITEM_EFFECT_EXPIRED,
  locksChanged: LOCKS_CHANGED,
  newInventoryItem: INVENTORY_ITEM_ADDED,
  progressChanged: REGION_PROGRESS_CHANGED,
  promotionAdded: PROMOTION_ADDED,
  promotionEnded: PROMOTION_ENDED,
  promotionPreviewStarted: PROMOTION_PREVIEW_STARTED,
  promotionPreviewEnded: PROMOTION_PREVIEW_ENDED,
  promotionStarted: PROMOTION_STARTED,
  regionChanged: REGION_SELECTED,
  season_ended: SEASON_ENDED,
  season_started: SEASON_STARTED,
  sessionExpired: SESSION_EXPIRED,
  storeItemDeleted: STORE_ITEM_DELETED,
  storeItemUpdated: STORE_ITEM_UPDATED,
  tableUpdated: TABLE_UPDATED,
  worldChanged: WORLD_SELECTED

  /**
   * Attach handlers to corresponding events
   * @private
   * @param {object} channel Pusher channel instance.
   * @param {SocketActionBindings} bindings Event-to-callback bindings.
   * @param {function} dispatch Redux dispatch.
   */
};function attachEventHandlers(channel, bindings, dispatch) {
  channel.bind_global(function (event, payload) {
    var type = EVENT_MAPPINGS[event];
    var callback = bindings[type];

    if (type) {
      dispatch({ type: type, payload: payload });

      if (typeof callback === 'function') {
        var effect = callback(payload);
        if (effect) {
          dispatch(effect);
        }
      }
    }
  });
}

/**
 * Redux middleware for websockets
 * @param {object} [options]
 * @param {object} [options.Pusher] Custom Pusher class
 * @param {SocketActionBindings} [options.bindings] Event-to-callback bindings.
 * @returns {function} Redux middleware
 */
function middleware() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  return function (_ref) {
    var dispatch = _ref.dispatch;

    var socket = {};
    var $Pusher = options.Pusher || Pusher;
    var bindings = options.bindings || {};

    return function (next) {
      return function (action) {
        if (action.type === SUBSCRIBE && socket.subscribe) {
          attachEventHandlers(socket.subscribe(action.payload), bindings, dispatch);
        }

        if (action.type === UNSUBSCRIBE && socket.unsubscribe) {
          socket.unsubscribe(action.payload);
        }

        if (action.type === INIT) {
          var sessionToken = path(['auth', 'params', 'sessionToken'], action.payload.params);
          var username = path(['auth', 'params', 'username'], action.payload.params);

          if (socket && socket.allChannels) {
            socket.allChannels().forEach(function (channel) {
              return channel.unbind_all();
            });

            socket.disconnect();
          }

          socket = new $Pusher(action.payload.key, action.payload.params);

          if (sessionToken) {
            attachEventHandlers(socket.subscribe('private-' + username), bindings, dispatch);
          }

          attachEventHandlers(socket.subscribe('main'), bindings, dispatch);
        } else {
          return next(action);
        }
      };
    };
  };
}

/**
 * Perform side effect and/or dispatch returned Redux action
 * @typedef {function} SocketActionCallback
 * @param {*} payload Websocket event payload
 * @returns {?object} Redux action or nothing
 * @example
 * // print event's payload
 * function updateTable(payload) {
 *   console.log(payload)
 * }
 *
 * // dispatch additional actions
 * function notify(payload) {
 *   return notifications.add({
 *     type: 'info',
 *     message: `${payload.title} is available`,
 *   })
 * }
 */

/**
 * Event callbacks.
 * @typedef {object.<SocketActionType, SocketActionCallback>} SocketActionBindings
 */

/**
 * Action to (re)initialize socket with specific configuration
 * @param {string} key Pusher key.
 * @param {object} params Configuration params for Pusher.
 * @see {@link https://github.com/pusher/pusher-js}
 */
function init(key, params) {
  return {
    type: INIT,
    payload: {
      key: key,
      params: params
    }
  };
}

/**
 * Action to subscribe to a channel
 * @param {string} channel Channel
 */
function subscribe(channel) {
  return {
    type: SUBSCRIBE,
    payload: channel
  };
}

/**
 * Action to unsubscribe to a channel
 * @param {string} channel Channel
 */
function unsubscribe(channel) {
  return {
    type: UNSUBSCRIBE,
    payload: channel
  };
}

export { init, subscribe, unsubscribe, middleware, INIT, SUBSCRIBE, UNSUBSCRIBE, SESSION_EXPIRED, SEASON_STARTED, SEASON_ENDED, STORE_ITEM_DELETED, STORE_ITEM_UPDATED, TABLE_UPDATED, PROMOTION_ADDED, ITEM_EFFECT_EXPIRED, INVENTORY_ITEM_ADDED, CAMPAIGN_STARTED, CAMPAIGN_ENDED, BET_BACK_PLACED, WORLD_SELECTED, REGION_PROGRESS_CHANGED, CAMPAIGNS_CHANGED, BALANCE_CHANGED, REGION_SELECTED, PROGRESS_CHANGED, LOCKS_CHANGED, NEW_INVENTORY_ITEM_ADDED, PROMOTION_STARTED, PROMOTION_ENDED, PROMOTION_PREVIEW_STARTED, PROMOTION_PREVIEW_ENDED };
